/**
 * FLIX CART — Bulletproof AJAX Cart Modal + Fly-to-Cart Animation
 * ─────────────────────────────────────────────────────────────────
 * ANIMATION GUARANTEE:
 *   The fly-to-cart animation is triggered EXCLUSIVELY by intercepting
 *   the native fetch() / XMLHttpRequest at the network level.
 *   It fires IF AND ONLY IF:
 *     1. The request URL contains "/cart/add"
 *     2. The HTTP response status is 2xx (success)
 *     3. A real user gesture preceded the request (isTrusted check)
 *
 *   It NEVER fires on:
 *     - Page load / DOMContentLoaded / window.onload
 *     - Cart initialisation / badge refresh
 *     - Qty changes / item removal / cart icon open
 *     - Any programmatic / synthetic event
 *
 * Zero external dependencies · Pure vanilla JS · RTL-safe · Scoped IIFE
 */
(function () {
  'use strict';

  /* ════════════════════════════════════════════════
     STEP 1 — NETWORK INTERCEPTORS (installed first)
     Override fetch + XHR BEFORE anything else runs.
     This is the authoritative trigger for animation.
  ════════════════════════════════════════════════ */

  /**
   * _flyPermit  — one-shot gate. Set true by interceptor on HTTP 200 /cart/add.
   *               Consumed (reset) the instant flyToCart() reads it.
   *               NO other code sets this.
   *
   * _flySnapshot — { rect, src } captured synchronously at user-click time.
   *               Cleared immediately after use or on failure.
   *
   * _flyActive   — mutex: one ghost at a time.
   */
  var _flyPermit   = false;
  var _flySnapshot = null;
  var _flyActive   = false;

  /* Store native fetch before ANY override */
  var _nativeFetch = window.fetch;

  /* Called by interceptors when /cart/add returns 2xx */
  function _onAddSuccess() {
    if (_flySnapshot) {
      _flyPermit = true;        /* open one-shot permit */
      flyToCart(_flySnapshot);  /* permit consumed inside flyToCart */
    }
    _flySnapshot = null;        /* always clear */

    /* Refresh badge + toast (uses _nativeFetch to skip re-intercepting) */
    _nativeFetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (cart) {
      if (!cart) return;
      window._fcmLastCart = cart;
      updateBadge(cart.item_count); /* pulse OK — only called from real-add path */
      toast('\u062a\u0645 \u0625\u0636\u0627\u0641\u0629 \u0627\u0644\u0645\u0646\u062a\u062c \u0625\u0644\u0649 \u0627\u0644\u0633\u0644\u0629', 'add');
    })
    .catch(function () {});
  }

  /* ── Override fetch ── */
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input
            : (input && typeof input.url === 'string') ? input.url
            : String(input);

    var promise = _nativeFetch.apply(this, arguments);

    if (url.indexOf('/cart/add') !== -1) {
      promise.then(function (response) {
        if (response && response.ok) _onAddSuccess();
        return response;
      }).catch(function () {});
    }

    return promise;
  };

  /* ── Override XMLHttpRequest ── */
  var _OrigXHR = window.XMLHttpRequest;
  function _PatchedXHR() {
    var _xhr  = new _OrigXHR();
    var _url  = '';
    var _open = _xhr.open.bind(_xhr);

    _xhr.open = function (method, url) {
      _url = String(url || '');
      return _open.apply(_xhr, arguments);
    };

    _xhr.addEventListener('load', function () {
      if (_url.indexOf('/cart/add') !== -1 && _xhr.status >= 200 && _xhr.status < 300) {
        _onAddSuccess();
      }
    });

    return _xhr;
  }
  _PatchedXHR.prototype = _OrigXHR.prototype;
  window.XMLHttpRequest = _PatchedXHR;

  /* ════════════════════════════════════════════════
     STEP 2 — CAPTURE IMAGE SNAPSHOT ON USER GESTURE
     Listen in capture phase so we get the rect BEFORE
     any async fetch gap. Only runs on real isTrusted events.
  ════════════════════════════════════════════════ */

  function resolveImageSource(triggerEl) {
    var imgEl = null;

    /* 1. PDP main image */
    var pdpMain = document.getElementById('pdp-main-image');
    if (pdpMain) {
      var r = pdpMain.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) imgEl = pdpMain;
    }

    /* 2. Card image ancestor */
    if (!imgEl && triggerEl) {
      var card = triggerEl.closest('.flix-product-card, .product-card, [data-product-card]');
      if (card) imgEl = card.querySelector('img');
    }

    /* 3. Trigger itself is an img */
    if (!imgEl && triggerEl && triggerEl.tagName === 'IMG') imgEl = triggerEl;

    if (!imgEl) return null;
    var rect = imgEl.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    return { rect: rect, src: imgEl.currentSrc || imgEl.src };
  }

  /* Capture on click (capture phase — before submit fires) */
  document.addEventListener('click', function (e) {
    if (!e.isTrusted) return;
    var btn = e.target.closest(
      '[data-add-to-cart], .flix-atc-btn, .add-to-cart-btn, .btn-add-to-cart, [type="submit"]'
    );
    if (!btn) return;
    if (!btn.closest('form[action="/cart/add"]')) return;
    _flySnapshot = resolveImageSource(btn); /* synchronous — valid rect */
  }, true);

  /* Capture on keyboard submit (Enter key, no click precedes it) */
  document.addEventListener('submit', function (e) {
    if (!e.isTrusted) return;
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if ((form.getAttribute('action') || '').split('?')[0] !== '/cart/add') return;
    if (!_flySnapshot) {
      _flySnapshot = resolveImageSource(form.querySelector('[type="submit"]'));
    }
  }, true);

  /* ════════════════════════════════════════════════
     CONFIG — injected by flix-cart-modal.liquid
  ════════════════════════════════════════════════ */
  var ROUTES = window.fcmRoutes || { root: '/', checkout: '/checkout', cart: '/cart' };

  /* ════════════════════════════════════════════════
     DOM REFS
  ════════════════════════════════════════════════ */
  var overlay    = document.getElementById('fcm-overlay');
  var panel      = document.getElementById('fcm-panel');
  var body       = document.getElementById('fcm-body');
  var closeBtn   = document.getElementById('fcm-close');
  var toastWrap  = document.getElementById('fcm-toasts');
  var cartBadge  = document.getElementById('cart-count');
  var headerBadge= document.getElementById('fcm-badge');

  if (!panel) { console.warn('[FlixCart] Modal panel not found'); return; }

  /* ════════════════════════════════════════════════
     MONEY FORMAT
     Shopify returns prices in cents (integers).
     We format them with the store currency symbol.
  ════════════════════════════════════════════════ */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    // Use Shopify's global formatter if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.fcmMoneyFormat);
    }
    // Fallback: basic format
    var fmt = window.fcmMoneyFormat || '{{amount}} SAR';
    return fmt.replace(/\{\{amount(_no_decimals)?\}\}/, amount);
  }

  /* ════════════════════════════════════════════════
     OPEN / CLOSE
  ════════════════════════════════════════════════ */
  /* Scrollbar width — calculated once, used to prevent layout jump when locking scroll */
  var _scrollbarW = window.innerWidth - document.documentElement.clientWidth;

  function openModal() {
    panel.classList.add('is-open');
    overlay.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');
    /* Lock scroll without layout shift: compensate scrollbar gap with padding */
    document.body.style.overflow = 'hidden';
    document.body.style.paddingInlineEnd = _scrollbarW + 'px';
    setTimeout(function () { if (closeBtn) closeBtn.focus(); }, 60);
  }

  function closeModal() {
    panel.classList.remove('is-open');
    overlay.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.body.style.paddingInlineEnd = '';
  }

  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (overlay)  overlay.addEventListener('click', closeModal);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && panel.classList.contains('is-open')) closeModal();
  });

  /* Intercept any /cart links */
  document.addEventListener('click', function (e) {
    var a = e.target.closest('a[href]');
    if (!a) return;
    var href = (a.getAttribute('href') || '').split('?')[0];
    if (href === '/cart') {
      e.preventDefault();
      fetchCart().then(renderBody).then(openModal);
    }
  });

  /* Delegated close on "continue" button */
  document.addEventListener('click', function (e) {
    if (e.target && e.target.id === 'fcm-continue') closeModal();
  });

  /* ════════════════════════════════════════════════
     FETCH /cart.js — SOURCE OF TRUTH
  ════════════════════════════════════════════════ */
  function fetchCart() {
    return _nativeFetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
    .then(function (r) {
      if (!r.ok) throw new Error('Cart fetch failed: ' + r.status);
      return r.json();
    })
    .catch(function (err) {
      console.error('[FlixCart] fetchCart error:', err);
      throw err;
    });
  }

  /* ════════════════════════════════════════════════
     RENDER BODY FROM CART DATA
  ════════════════════════════════════════════════ */
  function renderBody(cart) {
    if (!body) return;
    /* Always silent — renderBody is never called from a real-add path */
    updateBadgeSilent(cart.item_count);

    if (!cart.item_count || cart.item_count === 0) {
      body.innerHTML = renderEmpty();
    } else {
      body.innerHTML = renderFilled(cart);
    }

    bindBodyEvents();
  }

  /* ── Empty state HTML ── */
  function renderEmpty() {
    return '<div class="fcm-empty">' +
      '<div class="fcm-empty__icon-wrap">' +
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>' +
          '<line x1="3" y1="6" x2="21" y2="6"/>' +
          '<path d="M16 10a4 4 0 0 1-8 0"/>' +
        '</svg>' +
      '</div>' +
      '<p class="fcm-empty__title">\u0627\u0644\u0633\u0644\u0629 \u0641\u0627\u0631\u063a\u0629</p>' +
      '<p class="fcm-empty__sub">\u0644\u0645 \u062a\u0642\u0645 \u0628\u0625\u0636\u0627\u0641\u0629 \u0623\u064a \u0645\u0646\u062a\u062c\u0627\u062a \u0628\u0639\u062f</p>' +
      '<a href="' + esc(ROUTES.root) + '" class="fcm-empty__btn">\u0627\u0644\u0639\u0648\u062f\u0629 \u0644\u0644\u0645\u062a\u062c\u0631</a>' +
    '</div>';
  }

  /* ── Filled state HTML ── */
  function renderFilled(cart) {
    var itemsHtml = '';
    for (var i = 0; i < cart.items.length; i++) {
      itemsHtml += renderItem(cart.items[i]);
    }

    return '<div class="fcm-split">' +
      /* Right col: items */
      '<div class="fcm-items-col">' +
        '<ul class="fcm-items" aria-label="\u0645\u0646\u062a\u062c\u0627\u062a \u0627\u0644\u0633\u0644\u0629">' +
          itemsHtml +
        '</ul>' +
      '</div>' +
      /* Left col: summary */
      '<div class="fcm-summary-col">' +
        /* Totals */
        '<div class="fcm-totals">' +
          '<p class="fcm-totals__label">\u0645\u0644\u062e\u0635 \u0627\u0644\u0637\u0644\u0628</p>' +
          '<div class="fcm-totals__row">' +
            '<span class="fcm-totals__key">\u0627\u0644\u0645\u062c\u0645\u0648\u0639 \u0627\u0644\u0643\u0644\u064a</span>' +
            '<span class="fcm-totals__val">' + formatMoney(cart.total_price) + '</span>' +
          '</div>' +
        '</div>' +
        '<label class="fcm-terms" for="fcm-terms-check">' +
          '<input type="checkbox" class="fcm-terms__checkbox" id="fcm-terms-check" aria-label="\u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0627\u0644\u0634\u0631\u0648\u0637 \u0648\u0627\u0644\u0623\u062d\u0643\u0627\u0645">' +
          '<span class="fcm-terms__text">أوافق على أن هذه خدمة رقمية مخصصة غير قابلة للإلغاء أو الاسترداد بعد بدء التنفيذ، ويتم تسليمها إلكترونياً عبر رابط يُرسل إلى بريدي وإشعار الطلب خلال 7–14 يوم عمل، وأوافق على <a href="https://s.flix-store.com/policies/terms-of-service" class="fcm-terms__link" target="_blank" rel="noopener noreferrer">الشروط والأحكام</a>.</span>' +
        '</label>' +
        /* Checkout */
        '<button type="button" class="fcm-btn fcm-btn--primary" id="fcm-checkout-btn" data-href="' + esc(ROUTES.checkout) + '">' +
          '<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>' +
          '</svg>' +
          '\u0645\u062a\u0627\u0628\u0639\u0629 \u0639\u0645\u0644\u064a\u0629 \u0627\u0644\u062f\u0641\u0639' +
        '</button>' +
        '<button class="fcm-btn fcm-btn--secondary" id="fcm-continue" type="button">\u0627\u0644\u0627\u0633\u062a\u0645\u0631\u0627\u0631 \u0641\u064a \u0627\u0644\u062a\u0633\u0648\u0642</button>' +
      '</div>' +
    '</div>';
  }

  /* ── Single item HTML ── */
  function renderItem(item) {
    var imgSrc   = item.featured_image && item.featured_image.url
      ? item.featured_image.url + '&width=160'
      : 'https://via.placeholder.com/80';
    var title    = esc(item.product_title || item.title);
    var variant  = (item.variant_title && item.variant_title !== 'Default Title')
      ? '<span class="fcm-item__variant">' + esc(item.variant_title) + '</span>'
      : '';
    var price    = formatMoney(item.final_line_price);
    var key      = esc(item.key);
    var url      = esc(item.url);
    var qty      = item.quantity;

    return '<li class="fcm-item" data-key="' + key + '">' +
      '<a href="' + url + '" class="fcm-item__img-wrap" tabindex="-1">' +
        '<img src="' + imgSrc + '" alt="' + title + '" width="80" height="80" loading="lazy" class="fcm-item__img">' +
      '</a>' +
      '<div class="fcm-item__info">' +
        '<a href="' + url + '" class="fcm-item__title">' + title + '</a>' +
        variant +
        '<span class="fcm-item__price">' + price + '</span>' +
      '</div>' +
      '<div class="fcm-item__controls">' +
        '<button class="fcm-item__remove" data-key="' + key + '" aria-label="\u062d\u0630\u0641 \u0627\u0644\u0645\u0646\u062a\u062c" type="button">' +
          '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<polyline points="3 6 5 6 21 6"/>' +
            '<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>' +
            '<path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>' +
          '</svg>' +
        '</button>' +
        '<div class="fcm-qty">' +
          '<button class="fcm-qty__btn fcm-qty__minus" data-key="' + key + '" data-qty="' + (qty - 1) + '" aria-label="\u062a\u0642\u0644\u064a\u0644" type="button">\u2212</button>' +
          '<span class="fcm-qty__val">' + qty + '</span>' +
          '<button class="fcm-qty__btn fcm-qty__plus"  data-key="' + key + '" data-qty="' + (qty + 1) + '" aria-label="\u0632\u064a\u0627\u062f\u0629" type="button">+</button>' +
        '</div>' +
      '</div>' +
    '</li>';
  }

  /* ── Bind qty/remove events inside body ── */
  function bindBodyEvents() {
    if (!body) return;

    /* Quantity buttons */
    body.querySelectorAll('.fcm-qty__btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.dataset.key;
        var qty = parseInt(btn.dataset.qty, 10);
        if (!key) { console.warn('[FlixCart] qty btn missing data-key'); return; }
        if (isNaN(qty) || qty < 0) qty = 0;
        changeItem(key, qty, qty === 0 ? 'del' : null);
      });
    });

    /* Remove buttons */
    body.querySelectorAll('.fcm-item__remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.dataset.key;
        if (!key) { console.warn('[FlixCart] remove btn missing data-key'); return; }
        changeItem(key, 0, 'del');
      });
    });

    /* Checkout button — direct location redirect, no form, no conflicts */
    var checkoutBtn = body.querySelector('#fcm-checkout-btn');
    var termsCheckbox = body.querySelector('#fcm-terms-check');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', function () {
        if (checkoutBtn.disabled) return;
        if (termsCheckbox && !termsCheckbox.checked) {
          toast('\u064a\u0631\u062c\u0649 \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0627\u0644\u0634\u0631\u0648\u0637 \u0623\u0648\u0644\u0627\u064b \u0642\u0628\u0644 \u0625\u062a\u0645\u0627\u0645 \u0627\u0644\u0637\u0644\u0628', 'err');
          return;
        }
        checkoutBtn.disabled = true;
        checkoutBtn.style.opacity = '0.7';
        window.location.href = ROUTES.checkout || '/checkout';
      });
    }
  }

  /* ════════════════════════════════════════════════
     CART API — CHANGE ITEM
  ════════════════════════════════════════════════ */
  function changeItem(key, qty, action) {
    setBodyLoading(true);
    _nativeFetch('/cart/change.js', {
      method : 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body   : JSON.stringify({ id: key, quantity: qty })
    })
    .then(function (r) {
      if (!r.ok) throw new Error('change.js failed: ' + r.status);
      return r.json();
    })
    .then(function (cart) {
      renderBody(cart);
      if (action === 'del') toast('\u062a\u0645 \u062d\u0630\u0641 \u0627\u0644\u0645\u0646\u062a\u062c \u0645\u0646 \u0627\u0644\u0633\u0644\u0629', 'del');
    })
    .catch(function (err) {
      console.error('[FlixCart] changeItem error:', err);
      setBodyLoading(false);
      toast('\u062d\u062f\u062b \u062e\u0637\u0623\u060c \u064a\u0631\u062c\u0649 \u0627\u0644\u0645\u062d\u0627\u0648\u0644\u0629', 'err');
    });
  }

  /* ════════════════════════════════════════════════
     BADGE UPDATE
     updateBadgeSilent — count only, no animation (used on init/restore)
     updateBadge       — count + pulse animation (used only after real add)
  ════════════════════════════════════════════════ */
  function updateBadgeSilent(n) {
    var s = String(n);
    [cartBadge, headerBadge].forEach(function (el) {
      if (el) el.textContent = s;
    });
  }

  function updateBadge(n) {
    updateBadgeSilent(n);
    /* Pulse only for real additions — callers must be user-gesture paths */
    if (cartBadge && n > 0) {
      cartBadge.classList.remove('fcm-pulsing');
      void cartBadge.offsetWidth;
      cartBadge.classList.add('fcm-pulsing');
      setTimeout(function () { cartBadge.classList.remove('fcm-pulsing'); }, 500);
    }
  }

  /* ════════════════════════════════════════════════
     BODY LOADING STATE
  ════════════════════════════════════════════════ */
  function setBodyLoading(on) {
    if (!body) return;
    if (on) {
      body.innerHTML = '<div class="fcm-loading"><span class="fcm-loading__spinner"></span></div>';
    }
  }

  /* ════════════════════════════════════════════════
     TOAST — top-center, stacks vertically
  ════════════════════════════════════════════════ */
  /* Ensure a single top-center container exists */
  var _toastContainer = document.getElementById('fcm-toast-top');
  if (!_toastContainer) {
    _toastContainer = document.createElement('div');
    _toastContainer.id = 'fcm-toast-top';
    _toastContainer.setAttribute('aria-live', 'polite');
    _toastContainer.setAttribute('aria-atomic', 'false');
    document.body.appendChild(_toastContainer);
  }

  function toast(msg, type) {
    var el = document.createElement('div');
    el.className = 'fcm-toast fcm-toast--' + (type || 'add');
    el.innerHTML = '<span class="fcm-toast__dot"></span><span>' + msg + '</span>';
    _toastContainer.appendChild(el);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { el.classList.add('is-show'); });
    });
    setTimeout(function () {
      el.classList.remove('is-show');
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 380);
    }, 3000);
  }

  /* ════════════════════════════════════════════════
     HTML ESCAPE
  ════════════════════════════════════════════════ */
  function esc(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /* ════════════════════════════════════════════════
     FLY TO CART ANIMATION
     ─────────────────────────────────────────────
     Called ONLY from _onAddSuccess() (the network interceptor).
     _flyPermit is the one-shot gate — must be true (set by
     interceptor) for animation to proceed. Any other call path
     to flyToCart() is silently ignored because _flyPermit is false.
  ════════════════════════════════════════════════ */
  function flyToCart(snapshot) {
    /* Gate: only the interceptor can open this */
    if (!_flyPermit) return;
    _flyPermit = false; /* one-shot: consumed immediately */

    if (_flyActive) return;
    if (!snapshot) return;

    var dest = document.querySelector('.flix-header__cart');
    if (!dest) return;

    var src = snapshot.rect;
    var dst = dest.getBoundingClientRect();
    if (src.width === 0 || src.height === 0) return;

    var ghost = document.createElement('div');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.style.cssText = [
      'position:fixed',
      'left:'   + src.left   + 'px',
      'top:'    + src.top    + 'px',
      'width:'  + src.width  + 'px',
      'height:' + src.height + 'px',
      'z-index:99999',
      'pointer-events:none',
      'border-radius:8px',
      'overflow:hidden',
      'transition:transform 0.65s cubic-bezier(0.4,0,0.2,1),opacity 0.65s cubic-bezier(0.4,0,0.2,1)',
      'will-change:transform,opacity',
      'transform-origin:center center'
    ].join(';');

    var clone = document.createElement('img');
    clone.src = snapshot.src;
    clone.alt = '';
    clone.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;border-radius:inherit;';
    ghost.appendChild(clone);

    document.body.appendChild(ghost);
    _flyActive = true;

    var dx = (dst.left + dst.width  / 2) - (src.left + src.width  / 2);
    var dy = (dst.top  + dst.height / 2) - (src.top  + src.height / 2);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        ghost.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(0.06)';
        ghost.style.opacity   = '0';
      });
    });

    setTimeout(function () {
      if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
      _flyActive = false;
    }, 720);
  }

  /* ════════════════════════════════════════════════
     ATC FORM HANDLER — visual feedback + submit
     Provides button loading state for forms on this page.
     Animation is NOT triggered here — the fetch interceptor
     (_onAddSuccess) handles that independently.
  ════════════════════════════════════════════════ */
  var _adding = false;

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = (form.getAttribute('action') || '').split('?')[0];
    if (action !== '/cart/add') return;
    if (!e.isTrusted) return; /* programmatic submits pass through */

    e.preventDefault();
    e.stopPropagation();

    if (_adding) return;

    var variantInput = form.querySelector('[name="id"]');
    if (!variantInput || !variantInput.value) {
      toast('\u064a\u0631\u062c\u0649 \u0627\u062e\u062a\u064a\u0627\u0631 \u062e\u064a\u0627\u0631 \u0623\u0648\u0644\u0627', 'err');
      return;
    }

    _adding = true;

    var submitBtn = form.querySelector('[type="submit"]');
    var origText  = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = '...'; }

    /* Use window.fetch (our patched version) — the interceptor fires on success */
    window.fetch('/cart/add.js', {
      method     : 'POST',
      headers    : { 'Accept': 'application/json' },
      credentials: 'same-origin',
      body       : new FormData(form)
    })
    .then(function (r) {
      if (!r.ok) return r.json().then(function (d) { throw d; });
      return r.json();
      /* _onAddSuccess() fires automatically via the fetch interceptor */
    })
    .catch(function (err) {
      _flySnapshot = null; /* discard snapshot — no animation on failure */
      var msg = (err && err.description) || '\u062d\u062f\u062b \u062e\u0637\u0623\u060c \u064a\u0631\u062c\u0649 \u0627\u0644\u0645\u062d\u0627\u0648\u0644\u0629';
      console.error('[FlixCart] ATC error:', err);
      toast(msg, 'err');
    })
    .finally(function () {
      _adding = false;
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = origText; }
    });
  }, true);

  /* ════════════════════════════════════════════════
     OPEN MODAL — always fetch fresh then render
  ════════════════════════════════════════════════ */
  /* Override cart icon click (already set above) but also
     use cached cart if available to render instantly, then refresh */
  document.querySelectorAll('.flix-header__cart').forEach(function (el) {
    /* Remove existing listeners safely by re-assigning via stored ref */
    el._fcmHandler = el._fcmHandler || function (e) {
      e.preventDefault();
      /* Show immediately with cached or loading state */
      if (window._fcmLastCart) {
        renderBody(window._fcmLastCart);
      } else {
        setBodyLoading(true);
      }
      openModal();
      /* Always fetch fresh — use native fetch, bypass interceptor */
      _nativeFetch('/cart.js', { method:'GET', headers:{'Accept':'application/json'}, credentials:'same-origin' })
        .then(function(r){ return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          window._fcmLastCart = cart;
          renderBody(cart);
        })
        .catch(function(){});
    };
    el.addEventListener('click', el._fcmHandler);
  });

  /* ════════════════════════════════════════════════
     INIT — silent badge restore on page load
     Uses _nativeFetch directly — bypasses interceptor entirely.
     NEVER triggers animation or badge pulse.
  ════════════════════════════════════════════════ */
  _nativeFetch('/cart.js', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    credentials: 'same-origin'
  })
  .then(function (r) { return r.ok ? r.json() : null; })
  .then(function (cart) {
    if (!cart) return;
    window._fcmLastCart = cart;
    updateBadgeSilent(cart.item_count); /* SILENT — no pulse, no animation */
  })
  .catch(function () {
    /* Non-critical — badge stays at Liquid-rendered value */
  });

})();